import React, { useCallback, type CSSProperties } from 'react';
import { Button, Input } from '@/components/ui';
import { CrossIcon, SearchIcon } from './icons';
import styles from './AuditFilters.module.css';

export interface AuditSearchInputProps {
    value: string;
    onChange: (next: string) => void;
    placeholder: string;
    /** Accessible name for the ✕ affordance, e.g. "Xoá tìm kiếm". Falls back to
     *  the placeholder so the button is never unlabelled. */
    clearLabel?: string;
    className?: string;
    style?: CSSProperties;
}

/**
 * EAA-1821: the audit bar's search box.
 *
 * CONTROLLED and deliberately UNDEBOUNCED. The panel above it owns the debounce
 * because it also owns the request: debouncing here would make the input lag
 * behind the keystrokes (the box would render stale text), and it would give
 * the screen two independent delays — one before the field updates, one before
 * the fetch — which is how a search box ends up feeling broken at 300ms.
 *
 * Built on the kit `Input` rather than a raw `<input>`: the affix wrapper it
 * renders is exactly the "icon + text + clear" shell this design needs, and it
 * arrives already themed, focus-ringed and dark-theme aware.
 */
function AuditSearchInputBase({
    value,
    onChange,
    placeholder,
    clearLabel,
    className,
    style,
}: AuditSearchInputProps): React.ReactElement {
    const handleChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => onChange(event.target.value),
        [onChange]
    );

    const handleClear = useCallback(() => onChange(''), [onChange]);

    // EAA-1821: `is-filled` paints the same brand ring as `:focus-within`, so a
    // search that is narrowing the table still reads as active after the operator
    // tabs away — otherwise the only cue that a query is applied is the result
    // counter at the far end of the bar.
    const rootClassName = [styles.search, value ? 'is-filled' : '', className ?? '']
        .filter(Boolean)
        .join(' ');

    return (
        <Input
            className={rootClassName}
            style={style}
            // EAA-1846: antd puts `className` on the affix WRAPPER and spreads the
            // rest onto the inner <input>, so this id lands on the field itself —
            // which is the element the e2e suite types into.
            data-testid="audit-filter-search"
            value={value}
            onChange={handleChange}
            placeholder={placeholder}
            variant="borderless"
            prefix={<SearchIcon className={styles.searchIcon} />}
            suffix={
                value ? (
                    <Button
                        className={styles.searchClear}
                        aria-label={clearLabel ?? placeholder}
                        onClick={handleClear}
                    >
                        <CrossIcon size={12} />
                    </Button>
                ) : undefined
            }
        />
    );
}

export const AuditSearchInput = React.memo(AuditSearchInputBase);
